import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { SupabasePushDeviceStatus, SupabasePushSubscription } from "./supabase";
import {
  pushNotificationStatus,
  pushPermissionLabel,
  type PushNotificationStatus,
  type PushPermissionLabel
} from "../pushNotificationStatus";

export type PushPermissionStatus = PushNotificationStatus;

type UsePushNotificationsOptions = {
  enabled: boolean;
  userId?: string;
  storeSubscription: (subscription: SupabasePushSubscription) => Promise<void>;
  removeSubscription: (endpoint: string) => Promise<void>;
  getSubscriptionStatus: (endpoint: string) => Promise<SupabasePushDeviceStatus>;
  requestTestPush: () => Promise<string>;
};

const vapidPublicKey = (import.meta.env.VITE_VAPID_PUBLIC_KEY ?? "").trim();

const isIosDevice = () => {
  const platform = navigator.platform ?? "";
  return /iPhone|iPad|iPod/i.test(navigator.userAgent) || (platform === "MacIntel" && navigator.maxTouchPoints > 1);
};

const isInstalledPwa = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

const platformSupportsPush = () =>
  typeof window !== "undefined" &&
  window.isSecureContext &&
  "Notification" in window &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "ServiceWorkerRegistration" in window &&
  "showNotification" in ServiceWorkerRegistration.prototype &&
  (!isIosDevice() || isInstalledPwa());

const browserPermission = (): NotificationPermission | "unsupported" =>
  typeof Notification === "undefined" ? "unsupported" : Notification.permission;

const vapidKeyBuffer = (value: string) => {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = `${value}${padding}`.replace(/-/g, "+").replace(/_/g, "/");
  const decoded = window.atob(base64);
  return Uint8Array.from(decoded, (character) => character.charCodeAt(0)).buffer;
};

const serializeSubscription = (subscription: PushSubscription): SupabasePushSubscription => {
  const serialized = subscription.toJSON();
  const p256dh = serialized.keys?.p256dh;
  const auth = serialized.keys?.auth;
  if (!serialized.endpoint || !p256dh || !auth) throw new Error("Prehliadač nevytvoril úplnú push subscription.");
  return { endpoint: serialized.endpoint, p256dh, auth, userAgent: navigator.userAgent };
};

const readyServiceWorker = async () => {
  const existing = await navigator.serviceWorker.getRegistration("/");
  if (existing) return existing;
  return navigator.serviceWorker.ready;
};

export const usePushNotifications = ({
  enabled,
  userId,
  storeSubscription,
  removeSubscription,
  getSubscriptionStatus,
  requestTestPush
}: UsePushNotificationsOptions) => {
  const supported = useMemo(
    () => enabled && Boolean(userId) && Boolean(vapidPublicKey) && platformSupportsPush(),
    [enabled, userId]
  );
  const [status, setStatus] = useState<PushPermissionStatus>(() =>
    pushNotificationStatus(supported, browserPermission(), false)
  );
  const [permission, setPermission] = useState<PushPermissionLabel>(() =>
    pushPermissionLabel(supported, browserPermission())
  );
  const [deviceStatus, setDeviceStatus] = useState<SupabasePushDeviceStatus>({ registered: false });
  const [isWorking, setIsWorking] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const ensurePromiseRef = useRef<Promise<PushSubscription> | null>(null);

  const refreshDeviceStatus = useCallback(async (subscription?: PushSubscription | null) => {
    if (!supported || Notification.permission !== "granted") {
      setDeviceStatus({ registered: false });
      setStatus(pushNotificationStatus(supported, browserPermission(), false));
      return;
    }
    const registration = await readyServiceWorker();
    const current = subscription ?? await registration.pushManager.getSubscription();
    if (!current) {
      setDeviceStatus({ registered: false });
      setStatus("Vypnuté");
      return;
    }
    const currentStatus = await getSubscriptionStatus(current.endpoint);
    if (currentStatus.registered) {
      await storeSubscription(serializeSubscription(current));
    }
    setDeviceStatus(currentStatus);
    setStatus(pushNotificationStatus(supported, browserPermission(), currentStatus.registered));
  }, [getSubscriptionStatus, storeSubscription, supported]);

  const ensureSubscription = useCallback(async () => {
    if (!supported || Notification.permission !== "granted") {
      throw new Error("Push upozornenia nie sú v tomto prostredí povolené.");
    }
    if (ensurePromiseRef.current) return ensurePromiseRef.current;
    ensurePromiseRef.current = (async () => {
      const registration = await readyServiceWorker();
      const existing = await registration.pushManager.getSubscription();
      const subscription = existing ?? await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidKeyBuffer(vapidPublicKey)
      });
      await storeSubscription(serializeSubscription(subscription));
      await refreshDeviceStatus(subscription);
      return subscription;
    })();
    try {
      return await ensurePromiseRef.current;
    } finally {
      ensurePromiseRef.current = null;
    }
  }, [refreshDeviceStatus, storeSubscription, supported]);

  useEffect(() => {
    setPermission(pushPermissionLabel(supported, browserPermission()));
    setStatus(pushNotificationStatus(supported, browserPermission(), false));
    if (!supported || Notification.permission !== "granted") {
      setDeviceStatus({ registered: false });
      return;
    }
    void refreshDeviceStatus().catch(() => undefined);
  }, [refreshDeviceStatus, supported, userId]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      setPermission(pushPermissionLabel(supported, browserPermission()));
      void refreshDeviceStatus().catch(() => undefined);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refreshDeviceStatus, supported]);

  const enableNotifications = useCallback(async () => {
    setError("");
    setMessage("");
    if (!supported) {
      setStatus("Nepodporované");
      setError(
        isIosDevice() && !isInstalledPwa()
          ? "Na iPhone pridaj FISPRO OS na plochu a otvor ho ako aplikáciu. Potom môžeš zapnúť notifikácie."
          : vapidPublicKey
            ? "Push notifikácie nie sú v tomto prehliadači alebo režime dostupné."
            : "Chýba verejný VAPID kľúč pre push notifikácie."
      );
      return;
    }
    setIsWorking(true);
    try {
      const permission = await Notification.requestPermission();
      setPermission(pushPermissionLabel(supported, permission));
      if (permission === "granted") {
        await ensureSubscription();
        setStatus("Zapnuté");
        setMessage("Toto zariadenie je pripravené prijímať notifikácie.");
      } else if (permission === "denied") {
        setError("Povolenie bolo zamietnuté. Zmeniť ho môžeš v systémových nastaveniach notifikácií.");
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Push notifikácie sa nepodarilo aktivovať.");
    } finally {
      setIsWorking(false);
    }
  }, [ensureSubscription, supported]);

  const disableNotifications = useCallback(async () => {
    setError("");
    setMessage("");
    setIsWorking(true);
    try {
      let serverCleanupFailed = false;
      const registration = await navigator.serviceWorker.getRegistration("/");
      const subscription = await registration?.pushManager.getSubscription();
      if (subscription) {
        try {
          await removeSubscription(subscription.endpoint);
        } catch {
          serverCleanupFailed = true;
        } finally {
          await subscription.unsubscribe();
        }
      }
      setDeviceStatus({ registered: false });
      setStatus("Vypnuté");
      setMessage(serverCleanupFailed
        ? "Zariadenie je vypnuté lokálne. Server registráciu označí ako expirovanú pri najbližšom pokuse."
        : "Toto zariadenie už nebude dostávať FISPRO notifikácie.");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Notifikácie sa nepodarilo vypnúť.");
    } finally {
      setIsWorking(false);
    }
  }, [removeSubscription]);

  const sendTestNotification = useCallback(async () => {
    setError("");
    setMessage("");
    if (!deviceStatus.registered) {
      setError("Najprv zaregistruj toto zariadenie pre notifikácie.");
      return;
    }
    setIsWorking(true);
    try {
      await requestTestPush();
      setMessage("Testovacia notifikácia bola zaradená do serverového odoslania.");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Testovaciu notifikáciu sa nepodarilo zaradiť.");
    } finally {
      setIsWorking(false);
    }
  }, [deviceStatus.registered, requestTestPush]);

  const disconnectSubscription = useCallback(async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
    const registration = await navigator.serviceWorker.getRegistration("/").catch(() => undefined);
    const subscription = await registration?.pushManager.getSubscription();
    if (!subscription) return;
    try {
      await removeSubscription(subscription.endpoint);
    } finally {
      await subscription.unsubscribe().catch(() => false);
    }
  }, [removeSubscription]);

  return {
    status,
    permission,
    deviceRegistered: deviceStatus.registered,
    lastSuccessfulPushAt: deviceStatus.lastSuccessfulPushAt,
    lastFailureAt: deviceStatus.lastFailureAt,
    lastErrorCategory: deviceStatus.lastErrorCategory,
    isInstalledPwa: typeof window !== "undefined" && isInstalledPwa(),
    isIosDevice: typeof navigator !== "undefined" && isIosDevice(),
    isWorking,
    error,
    message,
    enableNotifications,
    disableNotifications,
    sendTestNotification,
    disconnectSubscription
  };
};
